import {
  ComplianceDocumentType_Create,
  ComplianceDocumentType_Update,
  deleteV1CompaniesCompanyIdComplianceDocumentTypesId,
  getV1CompaniesCompanyIdComplianceDocumentTypes,
  GetV1CompaniesCompanyIdComplianceDocumentTypesData,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdComplianceDocumentTypes,
  patchV1CompaniesCompanyIdComplianceDocumentTypesId,
  postV1CompaniesCompanyIdComplianceDocumentTypes,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import connection from '~services/connectionModule';
import { extractPagination, Pagination, PaginationLink } from '~services/pagination';
import { fetchAllApiPages } from '~utils/fetchAllPages';

import { ComplianceDocumentType } from './models';

export default function useComplianceDocumentTypes() {
  const [isLoading, setIsLoading] = useState(false);
  const [complianceDocumentTypes, setComplianceDocumentTypes] = useState<
    ComplianceDocumentType[]
  >([]);
  const [pagination, setPagination] = useState<Pagination>({
    limit: DEFAULT_API_PAGE_SIZE,
    page: 1,
  });

  const getAll = useCallback(
    async (companyId: string, link?: PaginationLink, cursor?: string) => {
      setIsLoading(true);
      try {
        const query: GetV1CompaniesCompanyIdComplianceDocumentTypesData['query'] = {
          'page[limit]': DEFAULT_API_PAGE_SIZE,
          ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
        };
        const response = await getV1CompaniesCompanyIdComplianceDocumentTypes({
          path: { 'company-id': companyId },
          query,
        });
        const data = (response.data.data ?? []).map(ComplianceDocumentType.parse);
        const paginationData = extractPagination(response);
        setComplianceDocumentTypes(data);
        setPagination(paginationData);
        return { data, pagination: paginationData };
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.compliance_document_types.failed_to_fetch_types'),
          );
        } catch {
          // Error already handled (toast + Sentry)
        }
        return {
          data: [] as ComplianceDocumentType[],
          pagination: { limit: DEFAULT_API_PAGE_SIZE, page: 1 } as Pagination,
        };
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  // In a managed-vendor context, `managedCompanyId` is the vendor whose types we want;
  // `companyId` is the requester's own (managing) company. Fetching the vendor via the
  // non-managed route 404s, so we route through the managed endpoint instead.
  const fetchAll = useCallback(async (companyId: string, managedCompanyId?: string) => {
    setIsLoading(true);
    try {
      const allTypes = await fetchAllApiPages(
        (query) =>
          managedCompanyId
            ? getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdComplianceDocumentTypes(
                {
                  path: {
                    'company-id': companyId,
                    'managed-company-id': managedCompanyId,
                  },
                  query,
                },
              )
            : getV1CompaniesCompanyIdComplianceDocumentTypes({
                path: { 'company-id': companyId },
                query,
              }),
        ComplianceDocumentType.parse,
      );

      setComplianceDocumentTypes(allTypes);
      return allTypes;
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.compliance_document_types.failed_to_fetch_types'),
        );
      } catch {
        // Error already handled (toast + Sentry)
      }
      return [] as ComplianceDocumentType[];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const create = async (
    companyId: string,
    body: ComplianceDocumentType_Create | Record<string, unknown>,
  ) => {
    try {
      const response = await postV1CompaniesCompanyIdComplianceDocumentTypes({
        path: { 'company-id': companyId },
        body: body as ComplianceDocumentType_Create,
      });
      const parsed = ComplianceDocumentType.parse(response.data.data);
      setComplianceDocumentTypes((prev) => [...prev, parsed]);
      return parsed;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.compliance_document_types.failed_to_save'),
      );
      throw error;
    }
  };

  const update = async (
    companyId: string,
    id: string,
    body: ComplianceDocumentType_Update | Record<string, unknown>,
  ) => {
    try {
      const response = await patchV1CompaniesCompanyIdComplianceDocumentTypesId({
        path: { 'company-id': companyId, id },
        body: body as ComplianceDocumentType_Update,
      });
      const parsed = ComplianceDocumentType.parse(response.data.data);
      setComplianceDocumentTypes((prev) =>
        prev.map((item) => (item.id === id ? parsed : item)),
      );
      return parsed;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.compliance_document_types.failed_to_save'),
      );
      throw error;
    }
  };

  const remove = async (companyId: string, id: string) => {
    try {
      await deleteV1CompaniesCompanyIdComplianceDocumentTypesId({
        path: { 'company-id': companyId, id },
      });
      setComplianceDocumentTypes((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.compliance_document_types.failed_to_delete'),
      );
    }
  };

  return {
    complianceDocumentTypes,
    create,
    fetchAll,
    getAll,
    isLoading,
    pagination,
    remove,
    update,
  };
}
